import { action, computed, makeAutoObservable, observable } from "mobx";
import { GridApi } from "ag-grid-community";

/**
 * Central MobX store for the Sheet component.
 *
 * Holds observable grid state that can be consumed by any descendant via
 * context.  Designed for extension – add new @observable fields and @action
 * mutators here as requirements grow (e.g. batch editing state, conflict
 * tracking, server-sync status).
 */
export class SheetStore {
  // ── Grid API ────────────────────────────────
  // Use .ref so MobX only tracks the reference assignment, not the
  // internals of the ag-grid API object (which must never be proxied).
  @observable.ref private _gridApi: GridApi | null = null;

  // ── Editing state ───────────────────────────
  @observable private _isEditing = false;
  @observable private _editingRowId: string | null = null;
  @observable private _editingColId: string | null = null;

  // ── Dirty tracking ──────────────────────────
  /** Set of row ids that have been modified since last save / reset. */
  @observable private _dirtyRowIds = new Set<string>();

  // ── Clipboard state ─────────────────────────
  @observable private _isPasting = false;
  @observable private _isCutting = false;

  // ── Undo / redo state ───────────────────────
  @observable private _undoSize = 0;
  @observable private _redoSize = 0;
  @observable private _isUndoing = false;
  @observable private _isRedoing = false;

  // ── Generic metadata ────────────────────────
  /** Generic metadata map for consumer-specific state. */
  @observable private _meta = new Map<string, unknown>();

  constructor() {
    makeAutoObservable(this);
  }

  // ────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────

  @action
  setGridApi(api: GridApi | null): void {
    this._gridApi = api;
  }

  @action
  setEditing(rowId: string | null, colId: string | null): void {
    this._isEditing = rowId !== null;
    this._editingRowId = rowId;
    this._editingColId = colId;
  }

  @action
  clearEditing(): void {
    this._isEditing = false;
    this._editingRowId = null;
    this._editingColId = null;
  }

  @action
  setPasting(value: boolean): void {
    this._isPasting = value;
  }

  @action
  setCutting(value: boolean): void {
    this._isCutting = value;
  }

  @action
  markRowDirty(rowId: string): void {
    this._dirtyRowIds.add(rowId);
  }

  @action
  clearDirtyRow(rowId: string): void {
    this._dirtyRowIds.delete(rowId);
  }

  @action
  clearAllDirty(): void {
    this._dirtyRowIds.clear();
  }

  @action
  setUndoing(value: boolean): void {
    this._isUndoing = value;
  }

  @action
  setRedoing(value: boolean): void {
    this._isRedoing = value;
  }

  @action
  syncUndoRedoCounts(): void {
    if (!this._gridApi) {
      return;
    }
    this._undoSize = this._gridApi.getCurrentUndoSize();
    this._redoSize = this._gridApi.getCurrentRedoSize();
  }

  @action
  setMeta<V>(key: string, value: V): void {
    this._meta.set(key, value);
  }

  @action
  deleteMeta(key: string): void {
    this._meta.delete(key);
  }

  @action
  clearMeta(): void {
    this._meta.clear();
  }

  // ────────────────────────────────────────────
  // Derived / computed
  // ────────────────────────────────────────────

  @computed
  get gridApi(): GridApi | null {
    return this._gridApi;
  }

  @computed
  get isEditing(): boolean {
    return this._isEditing;
  }

  @computed
  get isPasting(): boolean {
    return this._isPasting;
  }

  @computed
  get isCutting(): boolean {
    return this._isCutting;
  }

  /**
   * `true` while a clipboard operation (paste or cut) is in progress.
   * Useful for deferring expensive work (e.g. server sync) until the
   * batch of `cellValueChanged` events has finished.
   */
  @computed
  get isClipboardOperationInProgress(): boolean {
    return this._isPasting || this._isCutting;
  }

  @computed
  get isUndoing(): boolean {
    return this._isUndoing;
  }

  @computed
  get isRedoing(): boolean {
    return this._isRedoing;
  }

  /**
   * `true` while an undo or redo operation is in progress.
   * During this window, AG Grid fires intermediate `cellValueChanged`
   * events whose undo/redo counts may be stale. Use this flag to
   * defer count-syncing and dirty-tracking until the operation ends.
   */
  @computed
  get isUndoRedoInProgress(): boolean {
    return this._isUndoing || this._isRedoing;
  }

  @computed
  get editingRowId(): string | null {
    return this._editingRowId;
  }

  @computed
  get editingColId(): string | null {
    return this._editingColId;
  }

  @computed
  get dirtyRowIds(): string[] {
    return [...this._dirtyRowIds];
  }

  @computed
  get dirtyRowCount(): number {
    return this._dirtyRowIds.size;
  }

  @computed
  get hasDirtyRows(): boolean {
    return this._dirtyRowIds.size > 0;
  }

  @computed
  get undoSize(): number {
    return this._undoSize;
  }

  @computed
  get redoSize(): number {
    return this._redoSize;
  }

  @computed
  get canUndo(): boolean {
    return this._undoSize > 0;
  }

  @computed
  get canRedo(): boolean {
    return this._redoSize > 0;
  }

  // ────────────────────────────────────────────
  // Convenience API wrappers
  // ────────────────────────────────────────────

  isRowDirty(rowId: string): boolean {
    return this._dirtyRowIds.has(rowId);
  }

  getMeta<V>(key: string): V | undefined {
    return this._meta.get(key) as V | undefined;
  }

  hasMeta(key: string): boolean {
    return this._meta.has(key);
  }

  undo(): void {
    // Don't call syncUndoRedoCounts() here – AG Grid processes undo
    // asynchronously (undoStarted → cellValueChanged → undoEnded).
    // The _isUndoing flag is set in handleUndoStarted and cleared in
    // handleUndoEnded, where the counts are also synced.
    this._gridApi?.undoCellEditing();
  }

  redo(): void {
    // Same as undo – _isRedoing flag and counts are managed by the
    // redoStarted / redoEnded event handlers.
    this._gridApi?.redoCellEditing();
  }

  stopEditing(cancel = false): void {
    this._gridApi?.stopEditing(cancel);
  }

  /**
   * Clears all AG Grid interactive state — focused cell, cell selection,
   * and any active inline editor — in a single call.
   *
   * Use this whenever transitioning into a mode (e.g. column selection)
   * where cell focus and editing must not overlap.
   */
  clearInteractiveState(): void {
    if (!this._gridApi) {
      return;
    }
    this._gridApi.stopEditing();
    this._gridApi.clearFocusedCell();
    this._gridApi.clearCellSelection();
  }

  /** Full teardown – call on unmount. */
  cleanup(): void {
    this._gridApi = null;
    this._dirtyRowIds.clear();
    this.clearEditing();
    this._isPasting = false;
    this._isCutting = false;
    this._isUndoing = false;
    this._isRedoing = false;
    this._undoSize = 0;
    this._redoSize = 0;
    this._meta.clear();
  }
}
